import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Banknote,
  Building,
  Calendar,
  Check,
  Clock,
  BookOpen,
  Download,
  Layers3,
  MapPin,
  Shield,
  Waypoints,
  Users,
} from 'lucide-react'
import { ProgrammeModel } from '@/models/programme.model'
import { programmesService } from '@/services/programmesService'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrencyAmount } from '@/utils/currency'
import { cn } from '@/utils/cn'
import { paths } from '@/routes/path'

type FetchState = 'idle' | 'loading' | 'loaded' | 'error'

export function ProgrammeDetailsPage() {
  const { programmeId } = useParams<{ programmeId: string }>()
  const [fetchState, setFetchState] = useState<FetchState>('idle')
  const [programmes, setProgrammes] = useState<ProgrammeModel[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    const loadProgrammes = async () => {
      if (!programmeId) return
      setFetchState('loading')
      try {
        const data = await programmesService.getAllProgrammes({ requireAuth: true })
        if (mounted) {
          setProgrammes(data.data)
          setFetchState('loaded')
        }
      } catch (error) {
        if (mounted) {
          setErrorMessage((error as Error).message || 'Unable to load programme details.')
          setFetchState('error')
        }
      }
    }

    loadProgrammes()

    return () => {
      mounted = false
    }
  }, [programmeId])

  const programme = useMemo(
    () => programmes.find((item) => item.id === programmeId),
    [programmes, programmeId],
  )



  const primaryAccount = programme?.getPrimaryPaymentAccount()
  const priceLabel = programme?.priceLabel
  const paymentAccounts = useMemo(() => {
    const accs = new Map<string, any>()
    programme?.cohorts.forEach((cohort) => {
      cohort.payment_accounts.forEach((account) => {
        const key = `${account.bankName}-${account.accountNumber}`
        if (!accs.has(key)) {
          accs.set(key, account)
        }
      })
    })
    return Array.from(accs.values())
  }, [programme?.cohorts])
  const segments = programme?.segments ?? []
  const admissionRequirements = programme?.admission_requirements

  if (!programmeId) {
    return (
      <EmptyState
        title="Programme Not Found"
        message="We could not determine which programme to display."
      />
    )
  }

  if (fetchState === 'loading') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-secondary-500">
        Loading programme details…
      </div>
    )
  }

  if (fetchState === 'error') {
    return (
      <EmptyState
        title="Unable to load programme"
        message={errorMessage ?? 'Please try again later.'}
      />
    )
  }

  if (!programme) {
    return (
      <EmptyState
        title="Programme Not Found"
        message="The programme you are looking for does not exist or has been removed."
      />
    )
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 text-sm text-secondary-500">
            <Link to={paths.app.programmes.root.absolute} className="inline-flex items-center gap-2 text-secondary-500 hover:text-secondary-700">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
            <span>Programme</span>
            {programme.department_id && (
              <>
                <span className="text-secondary-300">/</span>
                <span className="inline-flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  {programme.department_id}
                </span>
              </>
            )}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">{programme.title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={programme.is_active ? 'bg-green-100 text-green-800' : 'bg-secondary-100 text-secondary-700'}>
              {programme.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="outline">Duration: {programme.duration ?? 'N/A'} {programme.duration_unit ?? ''}</Badge>
            {programme.updatedAt && (
              <Badge variant="outline">Updated {new Date(programme.updatedAt).toLocaleDateString()}</Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild>
            <Link to={paths.app.programmes.cohorts.build(programme.id ?? '')}>
              <Users className="mr-2 h-4 w-4" />
              Manage Cohorts
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to={paths.app.programmes.curriculum.build(programme.id ?? '')}>
              <BookOpen className="mr-2 h-4 w-4" />
              View Curriculum
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <a href={programme.buildApplyUrl()} target="_blank" rel="noopener noreferrer">
              Apply via Landing Form
            </a>
          </Button>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card className="border-secondary-200 bg-gradient-to-r from-[#7b2a40]/10 via-transparent to-transparent">
          <CardHeader>
            <CardTitle>Programme Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-secondary-600 leading-relaxed">
            {programme.description ? (
              <p>{programme.description}</p>
            ) : (
              <p>No additional description has been provided for this programme.</p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <OverviewItem
                icon={<Clock className="h-4 w-4 text-[#7b2a40]" />}
                label="Duration"
                value={programme.duration ? `${programme.duration} ${programme.duration_unit}` : 'Not specified'}
              />
              <OverviewItem
                icon={<Banknote className="h-4 w-4 text-[#7b2a40]" />}
                label="Programme Fee"
                value={priceLabel ?? 'Contact admissions'}
              />
              <OverviewItem
                icon={<Waypoints className="h-4 w-4 text-[#7b2a40]" />}
                label="Segments"
                value={segments.length > 0 ? `${segments.length} segment${segments.length > 1 ? 's' : ''}` : 'Not configured'}
              />
              <OverviewItem
                icon={<Shield className="h-4 w-4 text-[#7b2a40]" />}
                label="Requires JAMB?"
                value={admissionRequirements ? (admissionRequirements.requiresJamb ? 'Yes' : 'No') : 'Not specified'}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Key Dates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-secondary-600">
            <ScheduleItem icon={<Calendar className="h-4 w-4" />} label="Created" value={programme.createdAt ? new Date(programme.createdAt).toLocaleDateString() : 'Unknown'} />
            <ScheduleItem icon={<Calendar className="h-4 w-4" />} label="Updated" value={programme.updatedAt ? new Date(programme.updatedAt).toLocaleDateString() : 'Unknown'} />
            {programme.department_id && (
              <ScheduleItem icon={<Building className="h-4 w-4" />} label="Department ID" value={programme.department_id} />
            )}
            <ScheduleItem icon={<MapPin className="h-4 w-4" />} label="Programme ID" value={programme.id || ''} />
          </CardContent>
        </Card>
      </section>

      <section>
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>{programme.description || 'N/A'}</p>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Payment Accounts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {paymentAccounts.length === 0 ? (
              <p className="text-sm text-secondary-500">No payment accounts have been configured.</p>
            ) : (
              paymentAccounts.map((account, index) => (
                <div
                  key={`${account.accountNumber ?? index}-${account.bankName ?? 'bank'}`}
                  className="rounded-lg border border-secondary-200 p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-secondary-900">
                      {account.bankName ?? 'Bank'}
                    </div>
                    <div className="flex gap-2">
                      {account.is_default && <Badge>Default</Badge>}
                      {!account.is_active && <Badge variant="outline">Inactive</Badge>}
                    </div>
                  </div>
                  <dl className="mt-3 space-y-2 text-sm text-secondary-600">
                    <div className="flex justify-between gap-4">
                      <dt>Account Name</dt>
                      <dd className="font-medium text-secondary-900">{account.accountName ?? 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt>Account Number</dt>
                      <dd className="font-medium text-secondary-900">{account.accountNumber ?? 'N/A'}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt>Currency</dt>
                      <dd className="font-medium text-secondary-900">{account.currency ?? 'N/A'}</dd>
                    </div>
                    {account.instructions && (
                      <div>
                        <dt className="font-medium text-secondary-900">Instructions</dt>
                        <dd>{account.instructions}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Admission Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {admissionRequirements ? (
              <div className="space-y-3 text-sm text-secondary-600">
                <RequirementRow label="Requires JAMB" value={admissionRequirements.requiresJamb} />
                <RequirementRow label="Requires O-Level" value={admissionRequirements.requiresOLevel} />
                <div>
                  <div className="font-semibold text-secondary-900 mb-2">Required Documents</div>
                  {admissionRequirements.requiredDocuments?.length ? (
                    <ul className="grid gap-2">
                      {admissionRequirements.requiredDocuments.map((doc) => (
                        <li key={doc} className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-[#7b2a40]" />
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-secondary-500">No specific documents listed.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-secondary-500">Admission requirements have not been specified.</p>
            )}
          </CardContent>
        </Card>
      </section>

      <section>
        <Card>
          <CardHeader>
            <CardTitle>Programme Segments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {segments.length === 0 ? (
              <p className="text-sm text-secondary-500">No segments have been defined.</p>
            ) : (
              <ol className="space-y-3">
                {segments
                  .slice()
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((segment, index) => (
                    <li key={`${segment.name ?? 'segment'}-${index}`} className="rounded-lg border border-secondary-200 p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-secondary-900">
                          {segment.name ?? `Segment ${index + 1}`}
                        </div>
                        <Badge variant="outline">
                          {segment.length ?? 'N/A'} {segment.unit ?? 'units'}
                        </Badge>
                      </div>
                      {segment.description && (
                        <p className="mt-2 text-sm text-secondary-600">{segment.description}</p>
                      )}
                    </li>
                  ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  )
}

interface EmptyStateProps {
  title: string
  message: string
}

const EmptyState: React.FC<EmptyStateProps> = ({ title, message }) => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center space-y-4 text-center">
    <h1 className="text-2xl font-bold text-secondary-900">{title}</h1>
    <p className="max-w-md text-secondary-600">{message}</p>
    <Link to="/app/programmes">
      <Button variant="outline">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Go Back
      </Button>
    </Link>
  </div>
)

interface OverviewItemProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}

const OverviewItem: React.FC<OverviewItemProps> = ({ icon, label, value }) => (
  <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
    <div className="flex items-center gap-2 text-sm font-semibold text-secondary-900">
      {icon}
      {label}
    </div>
    <div className="mt-2 text-sm text-secondary-600">{value}</div>
  </div>
)

interface RequirementRowProps {
  label: string
  value: boolean
}

const RequirementRow: React.FC<RequirementRowProps> = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-md border border-secondary-200 px-3 py-2 text-sm text-secondary-700">
    <span>{label}</span>
    <Badge className={cn(value ? 'bg-green-100 text-green-800' : 'bg-secondary-100 text-secondary-700')}>
      {value ? 'Yes' : 'No'}
    </Badge>
  </div>
)

interface ScheduleItemProps {
  icon: React.ReactNode
  label: string
  value: string
}

const ScheduleItem: React.FC<ScheduleItemProps> = ({ icon, label, value }) => (
  <div className="flex items-center gap-2">
    <span className="text-secondary-400">{icon}</span>
    <div>
      <div className="text-xs text-secondary-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-medium text-secondary-800">{value}</div>
    </div>
  </div>
)

export default ProgrammeDetailsPage
